/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";

import { v4 as UUID } from "uuid";

import type {
    CreateRequestType,
    OptionsType,
    RequestComplete,
    RequestWithBodyType,
    ResponseType,
} from "../types/http";
import FetchError from "../utils/errors/FetchError";
import HttpError from "../utils/errors/HttpError";
import { makeUrl, serialize } from "../utils/http";
import { check } from "../utils/utils";

function createRequest<B>({
    method,
    body,
    accessToken,
    addRequestId = true,
    headers,
}: CreateRequestType<B>): RequestInit {
    const request: RequestInit = {};

    request.method = method;

    if (headers) {
        request.headers = headers;
    } else {
        request.headers = {};
    }

    if (["POST", "PATCH", "PUT"].indexOf(method) !== -1) {
        if (!("Content-Type" in request.headers)) {
            request.headers["Content-Type"] = "application/json";
        }

        if (body) {
            if (request.headers["Content-Type"] === "application/json") {
                request.body = JSON.stringify(body);
            } else if (request.headers["Content-Type"] === "application/x-www-form-urlencoded") {
                request.body = serialize(body);
            } else if (request.headers["Content-Type"] === "application/octet-stream") {
                request.body = body as any;
            }
        }
    }

    if (accessToken) {
        request.headers["Authorization"] = "Bearer " + accessToken;
    }

    if (addRequestId) {
        request.headers["X-Request-ID"] = `UI-${UUID()}`;
    }

    return request;
}

function checkBlobFileByHeaders(headers: Headers): boolean {
    return (
        headers.has("Content-Type") &&
        (("" + headers.get("Content-Type")).indexOf("application/octet-stream") !== -1 ||
            ("" + headers.get("Content-Type")).indexOf("application/pdf") !== -1)
    );
}

function handleThen<R>(
    request: RequestInit,
    response: Response,
    responseBody: any,
    resolve: (value: R) => void,
    reject: (reason?: any) => void
) {
    let resolvedValue = null;

    if (response.headers.has("Content-Type")) {
        if (("" + response.headers.get("Content-Type")).indexOf("application/json") !== -1) {
            if (check(responseBody)) {
                resolvedValue = JSON.parse(responseBody);
            }
        } else if (checkBlobFileByHeaders(response.headers)) {
            resolvedValue = responseBody;
        } else {
            reject(new HttpError(request, response, responseBody));
        }
    }

    resolve(resolvedValue);
}

function handleCatch(
    abortController: AbortController,
    reject: (reason?: any) => void,
    request: RequestInit,
    error: any,
    url: string
) {
    if (!abortController.signal.aborted) {
        reject(new FetchError(request, error, url));
    }
}

type PropsType = {
    onStartCall?: () => void;
    onEndCall?: () => void;
};

function useFetch({ onStartCall, onEndCall }: PropsType) {
    const makeCall = React.useCallback(
        <R>({
            url,
            request,
            abortController,
            resolve,
            reject,
            retry,
            options,
        }: {
            url: string;
            request: RequestInit;
            abortController: AbortController;
            resolve: (value: R) => void;
            reject: (reason?: HttpError) => void;
            retry: number;
            options?: OptionsType;
        }) => {
            request.signal = abortController.signal;

            if (options?.disableCallback !== true && onStartCall) {
                onStartCall();
            }

            fetch(url, request)
                .then((response) => {
                    let parser;

                    if (checkBlobFileByHeaders(response.headers)) {
                        parser = response.blob();
                    } else {
                        parser = response.text();
                    }

                    parser.then((responseBody) => {
                        if (response.ok) {
                            handleThen(request, response, responseBody, resolve, reject);
                        } else if (
                            (options?.manageRetry ||
                                (request.method === "GET" &&
                                    response.status !== 404 &&
                                    response.status !== 401 &&
                                    response.status !== 403)) &&
                            retry > 1
                        ) {
                            if ((request.headers as any)["X-Request-ID"]) {
                                (request.headers as any)["X-Request-ID"] = `UI-${UUID()}`;
                            }

                            setTimeout(
                                () =>
                                    makeCall({
                                        url,
                                        request,
                                        abortController,
                                        resolve,
                                        reject,
                                        retry: retry - 1,
                                        options,
                                    }),
                                1000
                            );
                        } else {
                            reject(new HttpError(request, response, responseBody));
                        }
                    });
                })
                .catch((error) => {
                    if ((options?.manageRetry || request.method === "GET") && retry > 1) {
                        if ((request.headers as any)["X-Request-ID"]) {
                            (request.headers as any)["X-Request-ID"] = `UI-${UUID()}`;
                        }

                        setTimeout(
                            () =>
                                makeCall({
                                    url,
                                    request,
                                    abortController,
                                    resolve,
                                    reject,
                                    retry: retry - 1,
                                    options,
                                }),
                            1000
                        );
                    } else {
                        handleCatch(abortController, reject, request, error, url);
                    }
                })
                .finally(() => {
                    if (options?.disableCallback !== true && onEndCall) {
                        onEndCall();
                    }
                });
        },
        [onStartCall, onEndCall]
    );

    const apiCall = React.useCallback(
        <R>({
            url,
            request,
            abortController,
            options,
        }: {
            url: string;
            request: RequestInit;
            abortController: AbortController;
            options?: OptionsType;
        }): Promise<R> => {
            return new Promise((resolve, reject) => {
                makeCall({ url, request, abortController, resolve, reject, retry: 3, options });
            });
        },
        [makeCall]
    );

    const apiCallWrapper = React.useCallback(
        <P, Q, R>({
            baseUrl,
            url,
            pathParams,
            queryParams,
            request,
            options,
        }: {
            baseUrl?: string;
            url: string;
            pathParams?: P;
            queryParams?: Q;
            request: RequestInit;
            options?: OptionsType;
        }): ResponseType<R> => {
            const abortController = new AbortController();

            return [
                apiCall({
                    url: makeUrl<P, Q>(`${baseUrl ?? ""}${url}`, pathParams, queryParams),
                    request,
                    abortController,
                    options,
                }),
                abortController,
            ];
        },
        [apiCall]
    );

    const get = React.useCallback(
        <P, Q, R>({
            baseUrl,
            url,
            pathParams,
            queryParams,
            accessToken,
            addRequestId,
            headers,
            options,
        }: RequestWithBodyType<P, Q, undefined> & RequestComplete): ResponseType<R> => {
            return apiCallWrapper<P, Q, R>({
                baseUrl,
                url,
                pathParams,
                queryParams,
                request: createRequest({ method: "GET", accessToken, addRequestId, headers }),
                options,
            });
        },
        [apiCallWrapper]
    );

    const del = React.useCallback(
        <P, Q, R>({
            baseUrl,
            url,
            pathParams,
            queryParams,
            accessToken,
            addRequestId,
            headers,
            options,
        }: RequestWithBodyType<P, Q, undefined> & RequestComplete): ResponseType<R> => {
            return apiCallWrapper<P, Q, R>({
                baseUrl,
                url,
                pathParams,
                queryParams,
                request: createRequest({ method: "DELETE", accessToken, addRequestId, headers }),
                options,
            });
        },
        [apiCallWrapper]
    );

    const post = React.useCallback(
        <P, Q, B, R>({
            baseUrl,
            url,
            pathParams,
            queryParams,
            body,
            accessToken,
            addRequestId,
            headers,
            options,
        }: RequestWithBodyType<P, Q, B> & RequestComplete): ResponseType<R> => {
            return apiCallWrapper<P, Q, R>({
                baseUrl,
                url,
                pathParams,
                queryParams,
                request: createRequest<B>({
                    method: "POST",
                    body,
                    accessToken,
                    addRequestId,
                    headers,
                }),
                options,
            });
        },
        [apiCallWrapper]
    );

    const patch = React.useCallback(
        <P, Q, B, R>({
            baseUrl,
            url,
            pathParams,
            queryParams,
            body,
            accessToken,
            addRequestId,
            headers,
            options,
        }: RequestWithBodyType<P, Q, B> & RequestComplete): ResponseType<R> => {
            return apiCallWrapper<P, Q, R>({
                baseUrl,
                url,
                pathParams,
                queryParams,
                request: createRequest<B>({
                    method: "PATCH",
                    body,
                    accessToken,
                    addRequestId,
                    headers,
                }),
                options,
            });
        },
        [apiCallWrapper]
    );

    const put = React.useCallback(
        <P, Q, B, R>({
            baseUrl,
            url,
            pathParams,
            queryParams,
            body,
            accessToken,
            addRequestId,
            headers,
            options,
        }: RequestWithBodyType<P, Q, B> & RequestComplete): ResponseType<R> => {
            return apiCallWrapper<P, Q, R>({
                baseUrl,
                url,
                pathParams,
                queryParams,
                request: createRequest<B>({
                    method: "PUT",
                    body,
                    accessToken,
                    addRequestId,
                    headers,
                }),
                options,
            });
        },
        [apiCallWrapper]
    );

    return React.useMemo(() => ({ get, del, post, patch, put }), [get, del, post, patch, put]);
}

export default useFetch;
